import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  USER_LOAN_ESTIMATION_DICT,
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  userLoanEstimationTypes,
  userLoanPrograms,
  userOccupancys,
} from '@/config/constants';
import { LOAN_PATH } from '@/config/loanNavPaths';
import {
  useGetCurrentUserQuery,
  useUpdateUserMutation,
} from '@/apis/extended/userApi';
import { CommonFormControl } from '../Common/CommonFormControl';
import { ModeToggle } from './ModeToggle';

export interface ProfileLoanTypeProps {
  onContinueToQualifications: () => void;
  /** `embedded` suppresses outer page heading so this can be re-mounted in a
   *  deal-room stage. Defaults to `page`. */
  variant?: 'page' | 'embedded';
  /**
   * Hides the left `ModeToggle` column (e.g. deal-room embed where workflow
   * is already implied). On `/loan/loan-type` the toggle is shown.
   */
  hideModeToggle?: boolean;
}

export const ProfileLoanType = ({
  onContinueToQualifications,
  variant: _variant = 'page',
  hideModeToggle = false,
}: ProfileLoanTypeProps) => {
  const navigate = useNavigate();
  const { data, isFetching } = useGetCurrentUserQuery();
  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation();

  const [form, setForm] = useState({
    loanEstimationType: USER_LOAN_ESTIMATION_TYPE.COMMERCIAL,
    loanProgram: USER_LOAN_PROGRAM.DEFAULT,
    occupancy: '',
  });

  useEffect(() => {
    setForm({
      loanEstimationType:
        data?.loanEstimationType || USER_LOAN_ESTIMATION_TYPE.COMMERCIAL,
      loanProgram: data?.loanProgram || USER_LOAN_PROGRAM.DEFAULT,
      occupancy: data?.occupancy || '',
    });
  }, [data?.loanEstimationType, data?.loanProgram, data?.occupancy]);

  const filteredUserLoanPrograms = useMemo(() => {
    const programs =
      USER_LOAN_ESTIMATION_DICT[form.loanEstimationType]?.LOAN_PROGRAMS || [];
    return userLoanPrograms.filter((p) => programs.includes(p.value));
  }, [form.loanEstimationType]);

  const filteredUserOccupancys = useMemo(() => {
    const occupancys =
      USER_LOAN_ESTIMATION_DICT[form.loanEstimationType]?.OCCUPANCY || [];
    return userOccupancys.filter((o) => occupancys.includes(o.value));
  }, [form.loanEstimationType]);

  const persistLoanType = async (next: typeof form) => {
    await updateUser({
      loanEstimationType: next.loanEstimationType || undefined,
      loanProgram: next.loanProgram || undefined,
      occupancy: next.occupancy || undefined,
    }).unwrap();
  };

  const handleEstimationTypeChange = (e: SelectChangeEvent) => {
    const loanEstimationType = e.target.value;
    const programs =
      USER_LOAN_ESTIMATION_DICT[loanEstimationType]?.LOAN_PROGRAMS || [];
    const validPrograms = userLoanPrograms.filter((p) =>
      programs.includes(p.value)
    );
    const occupancys =
      USER_LOAN_ESTIMATION_DICT[loanEstimationType]?.OCCUPANCY || [];
    const validOccupancys = userOccupancys.filter((o) =>
      occupancys.includes(o.value)
    );
    setForm((prev) => ({
      ...prev,
      loanEstimationType,
      loanProgram: validPrograms.some((p) => p.value === prev.loanProgram)
        ? prev.loanProgram
        : validPrograms[0]?.value || USER_LOAN_PROGRAM.DEFAULT,
      occupancy: validOccupancys.some((o) => o.value === prev.occupancy)
        ? prev.occupancy
        : validOccupancys[0]?.value || '',
    }));
  };

  const handleLoanProgramChange = async (e: SelectChangeEvent) => {
    const loanProgram = e.target.value;
    const next = { ...form, loanProgram };
    setForm(next);
    try {
      await persistLoanType(next);
      onContinueToQualifications();
    } catch {
      /* RTK will surface errors if configured */
    }
  };

  const handleOccupancyChange = (e: SelectChangeEvent) => {
    setForm({ ...form, occupancy: e.target.value });
  };

  const handleContinue = async () => {
    try {
      await persistLoanType(form);
      onContinueToQualifications();
    } catch {
      onContinueToQualifications();
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Card>
        <CardHeader
          title="Loan program & occupancy"
          avatar={<i className="fa-light fa-file-lines" />}
        />
        <CardContent>
          <Grid container spacing={3} alignItems="stretch">
            <Grid
              item
              xs={12}
              md={hideModeToggle ? 12 : 7}
              lg={hideModeToggle ? 12 : 8}
            >
              <CommonFormControl label="Loan estimation type:" labelWidth={180}>
                <Select
                  name="loanEstimationType"
                  value={form.loanEstimationType}
                  onChange={handleEstimationTypeChange}
                  disabled={isFetching}
                >
                  {userLoanEstimationTypes.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </CommonFormControl>

              <CommonFormControl
                label="Loan program:"
                sx={{ mt: 2 }}
                labelWidth={180}
              >
                <Select
                  name="loanProgram"
                  value={form.loanProgram}
                  onChange={handleLoanProgramChange}
                  disabled={isFetching || isSaving}
                >
                  {filteredUserLoanPrograms.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </CommonFormControl>

              <CommonFormControl
                label="Occupancy:"
                sx={{ mt: 2 }}
                labelWidth={180}
              >
                <Select
                  name="occupancy"
                  value={form.occupancy}
                  onChange={handleOccupancyChange}
                  disabled={isFetching}
                  displayEmpty
                >
                  <MenuItem value="">Select occupancy</MenuItem>
                  {filteredUserOccupancys.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </CommonFormControl>

              <CommonFormControl
                label="Documents:"
                labelWidth={180}
                sx={{ mt: 2 }}
              >
                <Button onClick={() => navigate(LOAN_PATH.documents)}>
                  Manage documents
                </Button>
              </CommonFormControl>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <LoadingButton
                  variant="contained"
                  loading={isSaving}
                  disabled={isFetching}
                  onClick={handleContinue}
                  color="primary"
                >
                  Continue to loan qualifications
                </LoadingButton>
              </Box>
            </Grid>
            {!hideModeToggle && (
              <Grid item xs={12} md={5} lg={4}>
                <ModeToggle embedded />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
